import type { BackendCapabilities } from "../backend/contracts";
import { LocalResourceClient } from "./resource-client";
import { createPermissionRecord, IndexedDbPermissionStore, MemoryPermissionStore, missingGrantedCapabilities, type AppPermissionRecord, type LocalPermissionStore } from "./permissions";
import {
  LOCAL_APP_MANIFEST_SCHEMA_VERSION,
  LOCAL_KERNEL_CONTRACT_VERSION,
  type KernelBackend,
  type LocalAppManifest,
  type LocalCapability,
  type LocalCapabilityStatus,
  type LocalResourceManifest,
  type LocalWorkspaceKernelDescriptor,
  type LocalWorkspaceKernelOptions,
} from "./contracts";

const APP_ID_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
const STORAGE_NAMESPACE_PATTERN = /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/;

export class LocalKernelError extends Error {
  constructor(
    public readonly code: "invalid_manifest" | "duplicate_app" | "unknown_app" | "missing_capability" | "unknown_resource" | "stale_permission" | "permission_unavailable",
    message: string,
  ) {
    super(message);
    this.name = "LocalKernelError";
  }
}

export class LocalWorkspaceKernel {
  private readonly apps = new Map<string, LocalAppManifest>();
  private readonly environment: Required<NonNullable<LocalWorkspaceKernelOptions["environment"]>>;
  private readonly configuredPermissionStore?: LocalPermissionStore;
  private permissionStorePromise?: Promise<LocalPermissionStore>;

  constructor(
    private readonly backend: KernelBackend,
    options: LocalWorkspaceKernelOptions = {},
  ) {
    this.configuredPermissionStore = options.permissionStore;
    this.environment = options.environment ?? {
      hasServiceWorker: typeof navigator !== "undefined" && "serviceWorker" in navigator,
      hasWorker: typeof Worker !== "undefined",
      hasBroadcastChannel: typeof BroadcastChannel !== "undefined",
    };
  }

  register(app: LocalAppManifest): void {
    validateManifest(app);
    if (this.apps.has(app.id)) {
      throw new LocalKernelError("duplicate_app", `App ${app.id} is already registered.`);
    }
    this.apps.set(app.id, structuredClone(app));
  }

  getApp(id: string): LocalAppManifest | undefined {
    const app = this.apps.get(id);
    return app ? structuredClone(app) : undefined;
  }

  requireApp(id: string): LocalAppManifest {
    const app = this.getApp(id);
    if (!app) {
      throw new LocalKernelError("unknown_app", `App ${id} is not registered.`);
    }
    return app;
  }

  openApp(id: string): LocalAppSession {
    return new LocalAppSession(this, this.backend, this.requireApp(id));
  }

  listApps(): readonly LocalAppManifest[] {
    return [...this.apps.values()]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((app) => structuredClone(app));
  }

  capabilityStatus(): readonly LocalCapabilityStatus[] {
    const capabilities: readonly LocalCapability[] = [
      "local-api",
      "persistent-storage",
      "offline-shell",
      "worker-execution",
      "snapshots",
      "cross-tab-sync",
      "cors-aware-outbound",
    ];
    return capabilities.map((capability) => ({ capability, available: supportsCapability(capability, this.backend.capabilities(), this.environment) }));
  }

  canRun(app: LocalAppManifest): boolean {
    const available = new Set(
      this.capabilityStatus()
        .filter((entry) => entry.available)
        .map((entry) => entry.capability),
    );
    return app.requiredCapabilities.every((capability) => available.has(capability));
  }

  async ensureResourceAccess(app: LocalAppManifest, resource: LocalResourceManifest, method: string): Promise<void> {
    await this.backend.start();
    if (!this.canRun(app)) {
      const missing = app.requiredCapabilities.filter((capability) => !this.capabilityStatus().find((entry) => entry.capability === capability)?.available);
      throw new LocalKernelError("missing_capability", `App ${app.id} is missing capability: ${missing.join(", ") || "runtime"}.`);
    }
    await this.ensurePermission(app);
    const operation = methodToOperation(method);
    if (!resource.operations.includes(operation)) {
      throw new LocalKernelError("missing_capability", `App ${app.id} is not allowed to ${operation} resource ${resource.id}.`);
    }
  }

  describe(): LocalWorkspaceKernelDescriptor {
    return {
      kind: "browser-local-kernel",
      contractVersion: LOCAL_KERNEL_CONTRACT_VERSION,
      generatedAt: new Date().toISOString(),
      capabilities: this.backend.capabilities(),
      capabilityStatus: this.capabilityStatus(),
      apps: this.listApps(),
    };
  }

  async listPermissionRecords(): Promise<readonly AppPermissionRecord[]> {
    return (await this.getPermissionStore()).list();
  }

  async regrantApp(id: string): Promise<AppPermissionRecord> {
    const app = this.requireApp(id);
    const store = await this.getPermissionStore();
    const record = createPermissionRecord(app);
    await store.put(record);
    return record;
  }

  private async ensurePermission(app: LocalAppManifest): Promise<AppPermissionRecord> {
    let store: LocalPermissionStore;
    try {
      store = await this.getPermissionStore();
    } catch (error) {
      throw new LocalKernelError("permission_unavailable", error instanceof Error ? error.message : "Permission registry is unavailable.");
    }
    const existing = await store.get(app.id);
    if (!existing) {
      const created = createPermissionRecord(app);
      await store.put(created);
      return created;
    }
    if (existing.manifestVersion !== app.version || existing.storageNamespace !== app.storageNamespace) {
      throw new LocalKernelError("stale_permission", `App ${app.id} permission must be migrated before this manifest can run.`);
    }
    const missing = missingGrantedCapabilities(app, existing);
    if (missing.length > 0) {
      throw new LocalKernelError("missing_capability", `App ${app.id} permission does not grant: ${missing.join(", ")}.`);
    }
    return existing;
  }

  private getPermissionStore(): Promise<LocalPermissionStore> {
    if (this.configuredPermissionStore) return Promise.resolve(this.configuredPermissionStore);
    if (!this.permissionStorePromise) {
      this.permissionStorePromise = typeof indexedDB === "undefined"
        ? Promise.resolve(new MemoryPermissionStore())
        : IndexedDbPermissionStore.open();
    }
    return this.permissionStorePromise;
  }
}

export class LocalAppSession {
  constructor(
    private readonly kernel: LocalWorkspaceKernel,
    private readonly backend: KernelBackend,
    public readonly manifest: LocalAppManifest,
  ) {}

  resource<TEntity extends { id: string; version: number }, TCreateInput extends object, TPatchInput extends object>(
    resourceId: string,
  ): LocalResourceClient<TEntity, TCreateInput, TPatchInput> {
    const resource = this.manifest.resources.find((candidate) => candidate.id === resourceId);
    if (!resource) {
      throw new LocalKernelError("unknown_resource", `Resource ${resourceId} is not declared by app ${this.manifest.id}.`);
    }
    return new LocalResourceClient<TEntity, TCreateInput, TPatchInput>(this.backend, resource.route, {
      beforeRequest: (method) => this.kernel.ensureResourceAccess(this.manifest, resource, method),
    });
  }
}

export function validateManifest(app: LocalAppManifest): void {
  if (app.schemaVersion !== LOCAL_APP_MANIFEST_SCHEMA_VERSION) {
    throw new LocalKernelError("invalid_manifest", "Unsupported local app manifest schema version.");
  }
  if (!APP_ID_PATTERN.test(app.id)) {
    throw new LocalKernelError("invalid_manifest", "App id must be lowercase kebab-case.");
  }
  if (!app.version.trim() || !app.label.trim() || !app.description.trim()) {
    throw new LocalKernelError("invalid_manifest", "App version, label and description are required.");
  }
  if (!Number.isInteger(app.dataSchemaVersion) || app.dataSchemaVersion < 1) {
    throw new LocalKernelError("invalid_manifest", "App data schema version must be a positive integer.");
  }
  if (!STORAGE_NAMESPACE_PATTERN.test(app.storageNamespace)) {
    throw new LocalKernelError("invalid_manifest", "Storage namespace must be lowercase and stable.");
  }
  if (app.offline && app.network === "online-required") {
    throw new LocalKernelError("invalid_manifest", "An offline app cannot require an online network mode.");
  }
  const resourceIds = new Set<string>();
  for (const resource of app.resources) {
    if (!resource.id.trim() || resourceIds.has(resource.id) || !resource.route.startsWith("/")) {
      throw new LocalKernelError("invalid_manifest", "Resource ids must be non-empty and unique per app.");
    }
    resourceIds.add(resource.id);
    if (!Number.isInteger(resource.version) || resource.version < 1 || resource.operations.length === 0) {
      throw new LocalKernelError("invalid_manifest", `Resource ${resource.id} has an invalid contract.`);
    }
  }
}

function supportsCapability(
  capability: LocalCapability,
  backend: BackendCapabilities,
  environment: Required<NonNullable<LocalWorkspaceKernelOptions["environment"]>>,
): boolean {
  switch (capability) {
    case "local-api":
      return backend.localApi;
    case "persistent-storage":
      return backend.persistence;
    case "offline-shell":
      return environment.hasServiceWorker;
    case "worker-execution":
      return environment.hasWorker;
    case "snapshots":
      return backend.persistence;
    case "cross-tab-sync":
      return environment.hasBroadcastChannel;
    case "cors-aware-outbound":
      return backend.outboundCorsAwareFetch;
  }
}

function methodToOperation(method: string): "list" | "get" | "create" | "update" | "delete" {
  switch (method) {
    case "GET": return "list";
    case "POST": return "create";
    case "PATCH": return "update";
    case "DELETE": return "delete";
    default: return "get";
  }
}
